import type { AdminExerciseDetail } from '@steado/admin-contract/exercises';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuth } from '@/lib/auth/auth-context';
import { Field, Fields, Panel } from '@/components/panel/panels';
import { formatDate } from '@/lib/format';
import { updateExercise } from '@/screens/exercises/exercises-query';

import { ExerciseFields } from './ExerciseFields';
import { SaveBar } from './SaveBar';
import {
  changedFields,
  countChanges,
  formIssues,
  formValuesFrom,
  type ExerciseFormValues,
} from './exercise-form';

interface OverviewTabProps {
  readonly exercise: AdminExerciseDetail;
  readonly onSaved: (exercise: AdminExerciseDetail) => void;
  /** Told when there is unsaved work, so the screen can guard leaving. */
  readonly onDirtyChange: (isDirty: boolean) => void;
}

/**
 * What the row claims to be — as a form, not a read-out.
 *
 * The console's other detail screens display and offer separate actions, but
 * this one exists to be corrected: a curator working the ~700-row import queue
 * is fixing a muscle map or a contraindication on most rows they open, and an
 * Edit button in front of every one of those is a click that buys nothing. The
 * fields are live; the save bar appears only once something has moved.
 */
export function OverviewTab({ exercise, onSaved, onDirtyChange }: OverviewTabProps) {
  const { api, can } = useAuth();

  const [original, setOriginal] = useState(() => formValuesFrom(exercise));
  const [values, setValues] = useState(original);
  const [snapshot, setSnapshot] = useState(exercise);

  // Re-baselined when the row itself changes. React Query keeps the reference
  // stable when a refetch returns identical data, so this fires on a real
  // change — a save of ours, or someone else's edit arriving — and not on
  // every background poll.
  if (snapshot !== exercise) {
    const next = formValuesFrom(exercise);
    setSnapshot(exercise);
    setOriginal(next);
    setValues(next);
    onDirtyChange(false);
  }

  const save = useMutation({
    mutationFn: () => updateExercise(api, exercise.id, changedFields(original, values)),
    onSuccess: (updated) => {
      onDirtyChange(false);
      onSaved(updated);
    },
    retry: false,
  });

  const changes = countChanges(original, values);
  const issues = formIssues(values);

  const onChange = (next: ExerciseFormValues) => {
    setValues(next);
    onDirtyChange(countChanges(original, next) > 0);
  };

  const discard = () => {
    setValues(original);
    onDirtyChange(false);
    save.reset();
  };

  return (
    <div className="flex flex-col gap-3">
      <ExerciseFields values={values} onChange={onChange} disabled={!can.mutate} />

      <Panel title="Provenance">
        <Fields>
          <Field label="Slug" value={exercise.slug} mono />
          <Field label="Added" value={formatDate(exercise.createdAt)} />
          <Field label="Last edited" value={formatDate(exercise.updatedAt)} />
          <Field
            label="Source"
            value={exercise.sourceId ?? 'hand-written'}
            mono={exercise.sourceId !== null}
          />
        </Fields>
        <p className="mt-2.5 text-[12px] leading-[1.55] text-muted-foreground">
          The slug is derived from the name when the row is created and then frozen. Plan generation is
          constrained to the slugs it knows, and a stored plan holds the one it was generated against, so
          renaming the movement changes what you read, not the key.
        </p>
      </Panel>

      <Panel title="Usage">
        <Fields>
          <Field label="Logged sets" value={String(exercise.usage.setLogs)} mono />
          <Field label="Planned slots" value={String(exercise.usage.plannedItems)} mono />
        </Fields>
        <p className="mt-2.5 text-[12px] leading-[1.55] text-muted-foreground">
          Training history references this movement, so deactivating keeps every logged set intact and only
          hides it from planning. Deleting is offered only while both counts are zero.
        </p>
      </Panel>

      {can.mutate ? (
        <SaveBar
          changes={changes}
          issues={issues}
          isPending={save.isPending}
          {...(save.isError ? { error: save.error.message } : {})}
          onSave={() => {
            save.mutate();
          }}
          onDiscard={discard}
        />
      ) : null}
    </div>
  );
}
